import { useParams, useSearchParams } from 'react-router-dom';

import { UndefinedInitialDataOptions, useQuery } from '@tanstack/react-query';

import { BASE_URL } from '@/shared/api/constants';
import { adaptProjectList } from '@/shared/api/project/adapters/adaptProjectList';
import { get } from '@/shared/api/requestMethods';

import type { GetProjectsResponse } from '../lib/types';

import { PROJECTS_LIMIT, RQ_PROJECTS_KEY } from '../lib/constants/common';

type GetProjectsParams = {
  name?: string;
  limit?: number;
  offset?: number;
  space_id?: string;
};

type GetProjectsAPIParams = {
  name?: string;
  page: number;
  space_id: string;
};

type GetProjectsListAPIParams = {
  name?: string;
  limit?: number;
  offset?: number;
  space_id: string;
};

export const getProjectsListAPI = ({
  name,
  limit = PROJECTS_LIMIT,
  offset = 0,
  space_id,
}: GetProjectsListAPIParams) => {
  return get<GetProjectsResponse, GetProjectsParams>(`${BASE_URL}/api/project/paginated`, {
    adapter: (response) => {
      const { data, offset: responseOffset, total } = response;

      return {
        data: adaptProjectList(data),
        offset: responseOffset,
        total,
      };
    },
    params: {
      name,
      limit,
      offset,
      space_id,
    },
  });
};

export const getProjectsAPI = ({ name, page, space_id }: GetProjectsAPIParams) => {
  return getProjectsListAPI({
    name,
    offset: page <= 1 ? 0 : (page - 1) * PROJECTS_LIMIT,
    space_id,
  });
};

export const useGetProjects = (
  params?: Omit<UndefinedInitialDataOptions<GetProjectsResponse>, 'queryFn' | 'queryKey'>,
) => {
  const [searchParams] = useSearchParams();
  const { spaceId } = useParams();

  const page = Number(searchParams.get('page')) || 1;
  const name = searchParams.get('searchText') || '';
  const space_id = spaceId || '';

  return useQuery({
    queryFn: () => getProjectsAPI({ name, page, space_id }),
    queryKey: [...RQ_PROJECTS_KEY, page, name, space_id],
    ...params,
  });
};
